/**
 * Slice 44 — Leave-out qty from guest count + plain-English "where" lines.
 * Owner math stays off the cleaner phone.
 */

export type LeaveOutMode = "fixed" | "per_guests";

export interface LeaveOutRule {
  mode: LeaveOutMode;
  /** Fixed: leave this many. Per guests: Q in "Leave Q every G guests". */
  qty: number;
  /** Per guests only — G in "every G guests". Defaults to 2 when missing. */
  per_guests?: number;
  /** Optional floor. Per guests defaults to `qty` when unset so never 0. */
  min_qty?: number | null;
  max_qty?: number | null;
}

export interface GuestAmenityPlacement {
  id: string;
  /** Plain label cleaners read, e.g. "Bathroom 1". */
  place_label: string;
  /**
   * Relative weight / base amount for this place when distributing leave-out.
   * Example: Bathroom 1 qty 1 + Bathroom 2 qty 1 → "1 in Bathroom 1 · 1 in Bathroom 2" when leave=2.
   */
  qty: number;
  property_room_id?: string | null;
}

export type ComputeLeaveOutResult = {
  leave_qty: number;
  /** True when guest count was missing and we fell back to minimum. */
  used_minimum: boolean;
};

function clampInt(n: number, min: number, max?: number | null): number {
  let v = Math.max(min, Math.floor(n));
  if (max != null && Number.isFinite(max)) v = Math.min(v, Math.floor(max));
  return v;
}

/** Normalize a rule; treat missing/invalid as fixed qty 1. */
export function normalizeLeaveOutRule(raw: unknown, fallbackFixedQty = 1): LeaveOutRule {
  const fallback = Math.max(0, Math.floor(fallbackFixedQty));
  if (!raw || typeof raw !== "object") {
    return { mode: "fixed", qty: fallback };
  }
  const o = raw as Record<string, unknown>;
  const mode: LeaveOutMode = o.mode === "per_guests" ? "per_guests" : "fixed";
  const qty =
    typeof o.qty === "number" && Number.isFinite(o.qty) && o.qty >= 0
      ? Math.floor(o.qty)
      : fallback;
  const per_guests =
    typeof o.per_guests === "number" && Number.isFinite(o.per_guests) && o.per_guests >= 1
      ? Math.floor(o.per_guests)
      : 2;
  const min_qty =
    typeof o.min_qty === "number" && Number.isFinite(o.min_qty) && o.min_qty >= 0
      ? Math.floor(o.min_qty)
      : null;
  const max_qty =
    typeof o.max_qty === "number" && Number.isFinite(o.max_qty) && o.max_qty >= 0
      ? Math.floor(o.max_qty)
      : null;
  return { mode, qty, per_guests, min_qty, max_qty };
}

/**
 * Compute how many to leave out for this stay.
 * Per guests: ceil(guests / G) × Q, then clamp to min/max.
 * Unknown guests → minimum (min_qty ?? Q for per_guests; fixed qty for fixed).
 */
export function computeLeaveOutQty(
  guestCount: number | null | undefined,
  rule: LeaveOutRule,
): ComputeLeaveOutResult {
  const qty = Math.max(0, Math.floor(rule.qty));
  const known =
    typeof guestCount === "number" && Number.isFinite(guestCount) && guestCount >= 1
      ? Math.floor(guestCount)
      : null;

  if (rule.mode === "fixed") {
    const min = rule.min_qty != null ? rule.min_qty : 0;
    return {
      leave_qty: clampInt(qty, min, rule.max_qty),
      used_minimum: false,
    };
  }

  const per = Math.max(1, Math.floor(rule.per_guests ?? 2));
  const defaultMin = qty > 0 ? qty : 0;
  const min = rule.min_qty != null ? Math.max(0, rule.min_qty) : defaultMin;

  if (known == null) {
    return {
      leave_qty: clampInt(min, 0, rule.max_qty),
      used_minimum: true,
    };
  }

  const raw = Math.ceil(known / per) * qty;
  return {
    leave_qty: clampInt(Math.max(raw, min), 0, rule.max_qty),
    used_minimum: false,
  };
}

/** Owner preview copy: "If 4 guests → leave 2" */
export function formatLeaveOutPreview(guestCount: number, rule: LeaveOutRule): string {
  const n = Math.max(1, Math.floor(guestCount));
  const { leave_qty } = computeLeaveOutQty(n, rule);
  return `If ${n} guests → leave ${leave_qty}`;
}

/** Owner rule echo: "Always 2" | "If 3 → leave 1" (Slice 44b). */
export function formatLeaveOutRuleSummary(rule: LeaveOutRule): string {
  if (rule.mode === "fixed") return `Always ${Math.max(0, Math.floor(rule.qty))}`;
  const q = Math.max(0, Math.floor(rule.qty));
  const g = Math.max(1, Math.floor(rule.per_guests ?? 2));
  return `If ${g} → leave ${q}`;
}

/** Read-only scale hint under Per guests (not a control). */
export function formatLeaveOutScaleHint(rule: LeaveOutRule): string | null {
  if (rule.mode !== "per_guests") return null;
  const x = Math.max(1, Math.floor(rule.per_guests ?? 2));
  const y = Math.max(1, Math.floor(rule.qty));
  const guests = [1, x, x + 1, x * 2].filter((v, i, a) => a.indexOf(v) === i).sort((a, b) => a - b);
  const parts = guests.map((g) => {
    const leave = computeLeaveOutQty(g, rule).leave_qty;
    return `${g} ${g === 1 ? "guest" : "guests"} → ${leave}`;
  });
  return `App scales it: ${parts.join(" · ")}`;
}

/**
 * Even split by pick order — remainder goes to the first room(s) picked.
 * Slice 44b: 1 roll across Bathroom 1 + Bathroom 2 → Bathroom 1.
 */
export function distributeEvenlyInOrder(
  leaveQty: number,
  placeLabels: string[],
): { place_label: string; qty: number }[] {
  const leave = Math.max(0, Math.floor(leaveQty));
  const labels = placeLabels.map((l) => l.trim()).filter(Boolean);
  if (leave <= 0 || labels.length === 0) return [];
  const base = Math.floor(leave / labels.length);
  let rem = leave % labels.length;
  return labels
    .map((place_label, i) => ({
      place_label,
      qty: base + (i < rem ? 1 : 0),
    }))
    .filter((p) => p.qty > 0);
}

/**
 * Distribute leaveQty across placement weights (largest-remainder).
 * Equal weights → even split by array order (Slice 44b).
 * Zero-qty results are dropped.
 */
export function distributePlacements(
  leaveQty: number,
  placements: Pick<GuestAmenityPlacement, "place_label" | "qty">[],
): { place_label: string; qty: number }[] {
  const leave = Math.max(0, Math.floor(leaveQty));
  const labeled = placements
    .map((p) => ({
      place_label: (p.place_label || "").trim(),
      qty: Math.max(0, Math.floor(p.qty)),
    }))
    .filter((p) => p.place_label);
  if (leave <= 0 || labeled.length === 0) return [];

  const weights = labeled.map((p) => p.qty);
  const totalW = weights.reduce((a, b) => a + b, 0);
  const allEqual =
    totalW <= 0 || weights.every((w) => w === weights[0]);

  if (allEqual) {
    return distributeEvenlyInOrder(
      leave,
      labeled.map((p) => p.place_label),
    );
  }

  const raw = weights.map((w) => (leave * w) / totalW);
  const floors = raw.map((r) => Math.floor(r));
  let rem = leave - floors.reduce((a, b) => a + b, 0);
  const order = raw
    .map((r, i) => ({ i, frac: r - floors[i]! }))
    .sort((a, b) => b.frac - a.frac);
  const out = floors.slice();
  for (let k = 0; k < rem; k++) {
    const idx = order[k % order.length]!.i;
    out[idx] = (out[idx] ?? 0) + 1;
  }
  return labeled
    .map((p, i) => ({ place_label: p.place_label, qty: out[i] ?? 0 }))
    .filter((p) => p.qty > 0);
}

/**
 * Cleaner-facing where line only — never includes owner math.
 * e.g. "1 in Bathroom 1 · 1 in Bathroom 2" or "2 in Kitchen"
 */
export function formatWhereLine(
  leaveQty: number,
  placements: Pick<GuestAmenityPlacement, "place_label" | "qty">[] | null | undefined,
  fallbackRoomLabel?: string | null,
): string | null {
  const leave = Math.max(0, Math.floor(leaveQty));
  if (leave <= 0) return null;

  if (placements && placements.length > 0) {
    const parts = distributePlacements(leave, placements);
    if (parts.length > 0) {
      return parts.map((p) => `${p.qty} in ${p.place_label}`).join(" · ");
    }
  }

  const room = (fallbackRoomLabel || "").trim();
  if (room) return `${leave} in ${room}`;
  return null;
}

/** Short recall for Inv: "You left out: 2 TP · 2 paper towels" */
export function formatLeftOutRecall(
  rows: { leave_qty: number; short_label: string }[],
): string | null {
  const parts = rows
    .filter((r) => r.leave_qty > 0 && r.short_label.trim())
    .map((r) => `${r.leave_qty} ${r.short_label.trim()}`);
  if (parts.length === 0) return null;
  return `You left out: ${parts.join(" · ")}`;
}

/** Shorten item labels for recall (Toilet paper → TP). */
export function shortLeaveOutLabel(itemLabel: string): string {
  const s = itemLabel.trim();
  const lower = s.toLowerCase();
  if (lower.includes("toilet") && lower.includes("paper")) return "TP";
  if (lower.includes("paper") && lower.includes("towel")) return "paper towels";
  if (lower.includes("hand") && lower.includes("soap")) return "soap";
  if (lower.includes("soap")) return "soap";
  if (s.length <= 18) return s;
  return `${s.slice(0, 16)}…`;
}
